import { type CSSProperties, type HTMLAttributes, useEffect, useState } from 'react'

import { useLoading } from '../Loading'
import { Container } from './Container'
import { Logo } from './Logo'
import { Placeholder } from './Placeholder'
import { SvgLogo } from './SvgLogo'

const getLetters = (title: string) => {
	const upperCaseLetters = title.replace(/[^A-Z0-9]/g, '')

	if (upperCaseLetters.length > 1) {
		return upperCaseLetters.substr(0, 2)
	}
	return title
		.replace(/[^a-zA-Z0-9]/g, '')
		.substr(0, 2)
		.toUpperCase()
}

type Props = Omit<HTMLAttributes<HTMLDivElement>, 'style'> & {
	background?: string | ((title: string) => string)
	border?: boolean
	imageUrl?: string | null
	size?: number | string
	title?: string
	square?: boolean
	className?: string
	style?: CSSProperties
}

export const ProductLogo = ({
	imageUrl,
	title,
	square,
	size = 50,
	className,
	style,
	border = true,
	background,
	...rest
}: Props) => {
	const isLoading = useLoading()
	const [imageState, setImageState] = useState<'empty' | 'loading' | 'ready' | 'error'>(
		isLoading || imageUrl ? 'loading' : 'empty',
	)

	useEffect(() => {
		if (!imageUrl) {
			setImageState('empty')
			return
		}

		setImageState('loading')

		const image = new Image()

		const onLoad = () => {
			setImageState('ready')
		}

		const onError = () => {
			setImageState('error')
		}

		image.addEventListener('load', onLoad)
		image.addEventListener('error', onError)

		image.src = imageUrl

		return () => {
			image.removeEventListener('load', onLoad)
			image.removeEventListener('error', onError)
		}
	}, [imageUrl])

	const showPlaceholder = isLoading || imageState === 'loading' || !title

	return (
		<Container
			className={className}
			style={style}
			size={typeof size === 'number' ? `${size}px` : size}
			square={square}
			{...rest}
		>
			{!isLoading && imageUrl && imageState !== 'error' && <Logo square={square} imageUrl={imageUrl} />}
			<Placeholder square={square} isHidden={!showPlaceholder} />
			{!isLoading && title && (imageState === 'empty' || imageState === 'error') && (
				<SvgLogo
					letters={getLetters(title)}
					background={typeof background === 'function' ? background(title) : background}
					square={square ?? false}
					borderWidth={border ? 0.04 : 0}
				/>
			)}
		</Container>
	)
}
